export type ItemArmorZone = "head" | "chest" | "stomach" | "waist" | "legs";

export interface ActiveItemEffects {
  hp: number;
  crit: number;
  antiCrit: number;
  dodge: number;
  antiDodge: number;
  armorActivationChance: number;
  armor: Record<ItemArmorZone, number>;
}

interface ItemRequirements {
  strength?: number;
  agility?: number;
  intuition?: number;
  endurance?: number;
}

export interface ItemArmorContext {
  slot: string;
  armor: number;
  requiredLevel?: number;
}

export const MAX_ARMOR_ACTIVATION_CHANCE = 70;

const armorChanceSlotBonus: Record<string, number> = {
  armor: 1.8,
  shield: 1.5,
  helmet: 1,
  legs: .8,
  boots: .45,
  gloves: .45,
  belt: .4,
  wrist: .4,
  shirt: .25,
  earrings: .2,
  necklace: .2,
  ring: 0,
  weapon: 0
};

const armorChanceItemCap: Record<string, number> = {
  armor: 10,
  shield: 9,
  helmet: 7,
  legs: 7,
  boots: 5,
  gloves: 5,
  belt: 5,
  wrist: 5,
  shirt: 4,
  earrings: 4,
  necklace: 4,
  ring: 3,
  weapon: 4
};

/**
 * Every protective item contributes a small, stable part of the full-set
 * activation chance. The value is derived from catalog facts so the same item
 * cannot become stronger or weaker merely because it was resold on the market.
 */
export function itemArmorActivationChance(armor: Record<ItemArmorZone, number>, context?: ItemArmorContext): number {
  if (!context) return 0;
  const explicitArmor = Object.values(armor).reduce((sum, value) => sum + Math.max(0, Number(value)), 0);
  // Legacy items with only the generic armor column protect five zones with
  // coefficients whose sum is 3.7 in the authoritative combat loader.
  const armorStrength = explicitArmor || Math.max(0, Number(context.armor)) * 3.7;
  if (armorStrength <= 0) return 0;
  const slot = context.slot || "armor";
  const level = Math.max(0, Math.min(7, Number(context.requiredLevel ?? 0)));
  const calculated = Math.round((.35 + level * .22 + Math.sqrt(armorStrength) * .38 + (armorChanceSlotBonus[slot] ?? .25)) * 1.48);
  return Math.max(1, Math.min(armorChanceItemCap[slot] ?? 5, calculated));
}

function textFromJson(raw: string | undefined): string {
  try { return String((JSON.parse(raw ?? "{}") as { text?: string }).text ?? ""); } catch { return ""; }
}

function objectFromJson(raw: string | undefined): ItemRequirements {
  try { return JSON.parse(raw ?? "{}") as ItemRequirements; } catch { return {}; }
}

function signedNumberFrom(text: string, pattern: RegExp): number {
  return Number(text.match(pattern)?.[1] ?? 0);
}

function armorFrom(text: string, labels: string): number {
  const match = text.match(new RegExp(`Броня (?:${labels}):\\s*(?:\\d+\\s*-\\s*)?([+-]?\\d+)`, "i"));
  return Number(match?.[1] ?? 0);
}

/** Единственный список текстовых эффектов, которые реально участвуют в серверном расчёте боя. */
export function parseActiveItemEffects(raw: string | undefined, context?: ItemArmorContext): ActiveItemEffects {
  const text = textFromJson(raw);
  const parsedArmor: Record<ItemArmorZone, number> = {
    head: armorFrom(text, "головы"),
    chest: armorFrom(text, "корпуса|груди"),
    stomach: armorFrom(text, "живота"),
    waist: armorFrom(text, "пояса"),
    legs: armorFrom(text, "ног")
  };
  // В раннем каталоге БК живот часто отсутствовал в выгрузке отдельной
  // строкой. Корпусная броня защищает его слабее груди; предметы с общей
  // защитой груди и пояса закрывают промежуточную зону средним значением.
  if (!parsedArmor.stomach && context) {
    if (context.slot === "armor" || context.slot === "shirt") {
      const bodyArmor = parsedArmor.chest || Math.max(0, Number(context.armor));
      parsedArmor.stomach = Math.round(bodyArmor * (context.slot === "armor" ? .8 : .65));
    } else if (parsedArmor.chest > 0 && parsedArmor.waist > 0) {
      parsedArmor.stomach = Math.round((parsedArmor.chest + parsedArmor.waist) / 2);
    }
  }
  return {
    hp: signedNumberFrom(text, /Уровень жизни \(HP\):\s*([+-]?\d+)/i),
    crit: signedNumberFrom(text, /Мф\. критического удара \(%\):\s*([+-]?\d+)/i),
    antiCrit: signedNumberFrom(text, /Мф\. против критического удара \(%\):\s*([+-]?\d+)/i),
    dodge: signedNumberFrom(text, /Мф\. увертывания \(%\):\s*([+-]?\d+)/i),
    antiDodge: signedNumberFrom(text, /Мф\. против увертывания \(%\):\s*([+-]?\d+)/i),
    armorActivationChance: itemArmorActivationChance(parsedArmor, context),
    armor: parsedArmor
  };
}

function signed(value: number): string { return `${value > 0 ? "+" : ""}${value}`; }

export function activeItemEffectLines(raw: string | undefined, context?: ItemArmorContext): string[] {
  const effect = parseActiveItemEffects(raw, context);
  const lines = [
    effect.hp ? `Уровень жизни (HP): ${signed(effect.hp)}` : "",
    effect.crit ? `Мф. критического удара (%): ${signed(effect.crit)}` : "",
    effect.antiCrit ? `Мф. против критического удара (%): ${signed(effect.antiCrit)}` : "",
    effect.dodge ? `Мф. увертывания (%): ${signed(effect.dodge)}` : "",
    effect.antiDodge ? `Мф. против увертывания (%): ${signed(effect.antiDodge)}` : "",
    effect.armorActivationChance ? `Вероятность срабатывания брони: +${effect.armorActivationChance}%` : ""
  ];
  const armorLabels: Record<ItemArmorZone, string> = { head: "Броня головы", chest: "Броня корпуса", stomach: "Броня живота", waist: "Броня пояса", legs: "Броня ног" };
  for (const zone of Object.keys(effect.armor) as ItemArmorZone[]) if (effect.armor[zone]) lines.push(`${armorLabels[zone]}: ${signed(effect.armor[zone])}`);
  return lines.filter(Boolean);
}

export function activeItemRequirementLines(raw: string | undefined): string[] {
  const requirements = objectFromJson(raw);
  const labels: Record<keyof ItemRequirements, string> = { strength: "Сила", agility: "Ловкость", intuition: "Интуиция", endurance: "Выносливость" };
  return (Object.keys(labels) as Array<keyof ItemRequirements>)
    .filter((key) => Number(requirements[key] ?? 0) > 0)
    .map((key) => `${labels[key]}: ${Number(requirements[key])}`);
}

export function itemEngraving(raw: string | undefined): string {
  try {
    const engraving = (JSON.parse(raw ?? "{}") as { engraving?: unknown }).engraving;
    return typeof engraving === "string" ? engraving.trim().slice(0, 240) : "";
  } catch { return ""; }
}
